import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArchive,
  FaBook,
  FaCheckCircle,
  FaClipboardCheck,
  FaPlus,
  FaSave,
  FaUsers,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import PageLoader from "../../../components/PageLoader";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";

const EMPTY_QUESTION = () => ({
  question_text: "",
  options: ["", "", "", ""],
  correct_index: 0,
  explanation: "",
});

const EMPTY_MODULE = {
  id: null,
  title: "",
  description: "",
  category: "General",
  content_url: "",
  content_notes: "",
  mandatory: false,
  quiz_enabled: false,
  passing_score: 80,
  estimated_minutes: 15,
  active: true,
};

function formatDateTime(value) {
  if (!value) return "No due date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No due date";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusForAssignment(assignment, progress) {
  if (progress?.status === "Completed") return "Completed";
  if (assignment?.due_at && new Date(assignment.due_at).getTime() < Date.now()) return "Overdue";
  return progress?.status || "Assigned";
}

export default function ManagementELearning({ session, profile }) {
  const [tab, setTab] = useState("catalogue");
  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [moduleDraft, setModuleDraft] = useState({ ...EMPTY_MODULE });
  const [questionDrafts, setQuestionDrafts] = useState([EMPTY_QUESTION()]);
  const [assignDraft, setAssignDraft] = useState({
    module_id: "",
    due_at: "",
    all_staff: true,
    all_management: false,
    roles: [],
    users: [],
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [roleSavingId, setRoleSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [moduleResult, questionResult, profileResult, assignmentResult, progressResult] = await Promise.all([
      supabase.from("e_learning_modules").select("*").order("created_at", { ascending: false }),
      supabase.from("e_learning_questions").select("*").order("position", { ascending: true }),
      supabase
        .from("profiles")
        .select("id, full_name, name_prefix, email, position, job_role, discord_id, suspended")
        .in("position", ["Staff", "Management"])
        .order("full_name", { ascending: true }),
      supabase
        .from("e_learning_assignments")
        .select(`
          id,
          module_id,
          user_id,
          due_at,
          assigned_at,
          assigned_via,
          assigned_role,
          revoked_at,
          module:e_learning_modules!e_learning_assignments_module_id_fkey(id, title, mandatory),
          user:profiles!e_learning_assignments_user_id_fkey(id, full_name, name_prefix, email, position, job_role)
        `)
        .order("assigned_at", { ascending: false }),
      supabase.from("e_learning_progress").select("*"),
    ]);

    const error = [moduleResult, questionResult, profileResult, assignmentResult, progressResult].find((result) => result.error)?.error;
    if (error) {
      console.error("Unable to load E-Learning management:", error);
      setFeedback({ type: "error", text: "E-Learning could not be loaded. Run ADD_STAFF_ELEARNING.sql first." });
    }

    setModules(moduleResult.data || []);
    setQuestions(questionResult.data || []);
    setProfiles((profileResult.data || []).filter((row) => row.suspended !== true));
    setAssignments(assignmentResult.data || []);
    setProgress(progressResult.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const progressMap = useMemo(
    () => Object.fromEntries(progress.map((row) => [row.assignment_id, row])),
    [progress],
  );

  const moduleCounts = useMemo(() => {
    const counts = {};
    for (const row of assignments) {
      counts[row.module_id] ||= { assigned: 0, complete: 0 };
      counts[row.module_id].assigned += 1;
      if (progressMap[row.id]?.status === "Completed") counts[row.module_id].complete += 1;
    }
    return counts;
  }, [assignments, progressMap]);

  const roleOptions = useMemo(
    () => [...new Set(profiles.map((row) => String(row.job_role || "").trim()).filter(Boolean))].sort(),
    [profiles],
  );

  function resetModuleDraft() {
    setModuleDraft({ ...EMPTY_MODULE });
    setQuestionDrafts([EMPTY_QUESTION()]);
  }

  function editModule(module) {
    setModuleDraft({
      ...EMPTY_MODULE,
      ...module,
      content_url: module.content_url || "",
      content_notes: module.content_notes || "",
      description: module.description || "",
    });
    const existing = questions
      .filter((question) => question.module_id === module.id)
      .sort((a, b) => a.position - b.position)
      .map((question) => ({
        id: question.id,
        question_text: question.question_text || "",
        options: Array.isArray(question.options) ? question.options : ["", "", "", ""],
        correct_index: Number(question.correct_index || 0),
        explanation: question.explanation || "",
      }));
    setQuestionDrafts(existing.length ? existing : [EMPTY_QUESTION()]);
    setTab("catalogue");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateQuestion(index, patch) {
    setQuestionDrafts((current) =>
      current.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question,
      ),
    );
  }

  function updateOption(questionIndex, optionIndex, value) {
    setQuestionDrafts((current) =>
      current.map((question, index) => {
        if (index !== questionIndex) return question;
        const nextOptions = [...question.options];
        nextOptions[optionIndex] = value;
        return { ...question, options: nextOptions };
      }),
    );
  }

  async function saveModule(event) {
    event.preventDefault();
    setFeedback(null);

    const quizEnabled = moduleDraft.mandatory ? true : moduleDraft.quiz_enabled;
    const cleanQuestions = questionDrafts
      .map((question) => ({
        ...question,
        question_text: question.question_text.trim(),
        options: question.options.map((option) => option.trim()),
        explanation: question.explanation.trim(),
      }))
      .filter((question) => question.question_text || question.options.some(Boolean));

    if (!moduleDraft.title.trim()) {
      setFeedback({ type: "error", text: "Enter a title for the learning module." });
      return;
    }

    if (quizEnabled) {
      if (cleanQuestions.length === 0) {
        setFeedback({ type: "error", text: "This learning requires at least one quiz question." });
        return;
      }
      const incomplete = cleanQuestions.some(
        (question) => !question.question_text || question.options.length !== 4 || question.options.some((option) => !option),
      );
      if (incomplete) {
        setFeedback({ type: "error", text: "Complete the question and all four answers for every quiz question." });
        return;
      }
    }

    setWorking(true);
    const payload = {
      title: moduleDraft.title.trim(),
      description: moduleDraft.description.trim() || null,
      category: moduleDraft.category.trim() || "General",
      content_url: moduleDraft.content_url.trim() || null,
      content_notes: moduleDraft.content_notes.trim() || null,
      mandatory: moduleDraft.mandatory,
      quiz_enabled: quizEnabled,
      passing_score: Number(moduleDraft.passing_score) || 80,
      estimated_minutes: Number(moduleDraft.estimated_minutes) || null,
      active: moduleDraft.active !== false,
      created_by: moduleDraft.id ? undefined : session?.user?.id || null,
    };
    if (payload.created_by === undefined) delete payload.created_by;

    let moduleId = moduleDraft.id;
    let moduleError = null;
    if (moduleId) {
      const result = await supabase.from("e_learning_modules").update(payload).eq("id", moduleId);
      moduleError = result.error;
    } else {
      const result = await supabase.from("e_learning_modules").insert(payload).select("id").single();
      moduleError = result.error;
      moduleId = result.data?.id;
    }

    if (moduleError || !moduleId) {
      setWorking(false);
      setFeedback({ type: "error", text: moduleError?.message || "The learning module could not be saved." });
      return;
    }

    const { error: deleteQuestionError } = await supabase
      .from("e_learning_questions")
      .delete()
      .eq("module_id", moduleId);

    if (deleteQuestionError) {
      setWorking(false);
      setFeedback({ type: "error", text: deleteQuestionError.message });
      return;
    }

    if (quizEnabled && cleanQuestions.length > 0) {
      const { error: insertQuestionError } = await supabase.from("e_learning_questions").insert(
        cleanQuestions.map((question, index) => ({
          module_id: moduleId,
          question_text: question.question_text,
          options: question.options,
          correct_index: Number(question.correct_index),
          explanation: question.explanation || null,
          position: index,
        })),
      );
      if (insertQuestionError) {
        setWorking(false);
        setFeedback({ type: "error", text: insertQuestionError.message });
        return;
      }
    }

    setWorking(false);
    setFeedback({ type: "success", text: moduleDraft.id ? "Learning module updated." : "Learning module created." });
    resetModuleDraft();
    await load();
  }

  async function toggleModuleActive(module) {
    const { error } = await supabase
      .from("e_learning_modules")
      .update({ active: !module.active })
      .eq("id", module.id);
    if (error) setFeedback({ type: "error", text: error.message });
    else await load();
  }

  async function saveJobRole(person) {
    setRoleSavingId(person.id);
    const { error } = await supabase.rpc("set_staff_job_role", {
      target_user_id: person.id,
      new_job_role: String(person.job_role || "").trim(),
    });
    setRoleSavingId(null);
    if (error) setFeedback({ type: "error", text: error.message });
    else setFeedback({ type: "success", text: `Role saved for ${formatProfileName(person)}.` });
  }

  function toggleArrayValue(field, value) {
    setAssignDraft((current) => {
      const exists = current[field].includes(value);
      return {
        ...current,
        [field]: exists
          ? current[field].filter((item) => item !== value)
          : [...current[field], value],
      };
    });
  }

  const resolvedTargets = useMemo(() => {
    const ids = new Set(assignDraft.users);
    if (assignDraft.all_staff) profiles.filter((row) => row.position === "Staff").forEach((row) => ids.add(row.id));
    if (assignDraft.all_management) profiles.filter((row) => row.position === "Management").forEach((row) => ids.add(row.id));
    if (assignDraft.roles.length) {
      profiles
        .filter((row) => assignDraft.roles.includes(String(row.job_role || "").trim()))
        .forEach((row) => ids.add(row.id));
    }
    return profiles.filter((row) => ids.has(row.id));
  }, [assignDraft, profiles]);

  async function assignLearning(event) {
    event.preventDefault();
    setFeedback(null);
    const module = modules.find((row) => row.id === assignDraft.module_id);
    if (!module) {
      setFeedback({ type: "error", text: "Select a learning module." });
      return;
    }
    if (!module.active) {
      setFeedback({ type: "error", text: "Archived learning cannot be newly assigned." });
      return;
    }
    if (module.mandatory && !assignDraft.due_at) {
      setFeedback({ type: "error", text: "Mandatory learning must have a due date." });
      return;
    }
    if (resolvedTargets.length === 0) {
      setFeedback({ type: "error", text: "Choose at least one staff member, position or role." });
      return;
    }

    setWorking(true);
    const targetIds = resolvedTargets.map((row) => row.id);
    const { data: existingRows, error: existingError } = await supabase
      .from("e_learning_assignments")
      .select("id, user_id, revoked_at")
      .eq("module_id", module.id)
      .in("user_id", targetIds)
      .is("revoked_at", null);

    if (existingError) {
      setWorking(false);
      setFeedback({ type: "error", text: existingError.message });
      return;
    }

    const existingIds = (existingRows || []).map((row) => row.id);
    let existingProgress = [];
    if (existingIds.length) {
      const result = await supabase
        .from("e_learning_progress")
        .select("assignment_id, status")
        .in("assignment_id", existingIds);
      if (result.error) {
        setWorking(false);
        setFeedback({ type: "error", text: result.error.message });
        return;
      }
      existingProgress = result.data || [];
    }

    const existingStatusMap = Object.fromEntries(
      existingProgress.map((row) => [row.assignment_id, row.status]),
    );
    const usersWithOutstanding = new Set(
      (existingRows || [])
        .filter((row) => existingStatusMap[row.id] !== "Completed")
        .map((row) => row.user_id),
    );
    const newTargets = resolvedTargets.filter((row) => !usersWithOutstanding.has(row.id));

    if (newTargets.length === 0) {
      setWorking(false);
      setFeedback({ type: "warning", text: "Everyone selected already has an outstanding assignment for this learning." });
      return;
    }

    const via = [
      assignDraft.all_staff ? "Staff" : null,
      assignDraft.all_management ? "Management" : null,
      assignDraft.roles.length ? `Roles: ${assignDraft.roles.join(", ")}` : null,
      assignDraft.users.length ? "Individuals" : null,
    ].filter(Boolean).join(" · ") || "Individual";

    const dueAt = assignDraft.due_at ? new Date(assignDraft.due_at).toISOString() : null;
    const { error: insertError } = await supabase.from("e_learning_assignments").insert(
      newTargets.map((person) => ({
        module_id: module.id,
        user_id: person.id,
        due_at: dueAt,
        assigned_by: session?.user?.id || null,
        assigned_via: via,
        assigned_role: person.job_role || person.position || null,
      })),
    );

    setWorking(false);
    if (insertError) {
      setFeedback({ type: "error", text: insertError.message });
      return;
    }

    const skipped = resolvedTargets.length - newTargets.length;
    setFeedback({
      type: "success",
      text: `Assigned “${module.title}” to ${newTargets.length} account${newTargets.length === 1 ? "" : "s"}${skipped ? `; ${skipped} already had it outstanding and were skipped` : ""}.`,
    });
    setAssignDraft((current) => ({ ...current, users: [], roles: [] }));
    await load();
  }

  async function revokeAssignment(id) {
    const { error } = await supabase
      .from("e_learning_assignments")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) setFeedback({ type: "error", text: error.message });
    else await load();
  }

  const filteredAssignments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assignments;
    return assignments.filter((row) =>
      [formatProfileName(row.user, ""), row.user?.email, row.user?.job_role, row.module?.title, row.user?.position]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [assignments, search]);

  if (loading) return <PageLoader label="Loading E-Learning management" />;

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />
      <main className="pguh-main">
        <div className="nhsuk-width-container pguh-elearning-management">
          <section className="pguh-dashboard-hero">
            <div>
              <span className="pguh-eyebrow">Management</span>
              <h1 className="nhsuk-heading-xl">Staff E-Learning</h1>
              <p className="nhsuk-body-l">
                Build training, create mandated quizzes, target roles or individuals, and monitor compliance.
              </p>
            </div>
            <div className="pguh-dashboard-identity">
              <span>Active modules</span>
              <strong>{modules.filter((row) => row.active).length}</strong>
              <small>{assignments.filter((row) => !row.revoked_at).length} assignments</small>
            </div>
          </section>

          {feedback && (
            <div className={`pguh-elearning-feedback pguh-elearning-feedback--${feedback.type}`} role="status">
              {feedback.text}
            </div>
          )}

          <nav className="pguh-operations-tabs pguh-learning-management-tabs" aria-label="E-Learning management sections">
            <button className={tab === "catalogue" ? "is-active" : ""} onClick={() => setTab("catalogue")} type="button"><FaBook /> Catalogue & quizzes</button>
            <button className={tab === "assign" ? "is-active" : ""} onClick={() => setTab("assign")} type="button"><FaUsers /> Assign learning</button>
            <button className={tab === "compliance" ? "is-active" : ""} onClick={() => setTab("compliance")} type="button"><FaClipboardCheck /> Compliance</button>
          </nav>

          {tab === "catalogue" && (
            <div className="pguh-elearning-management-grid">
              <form className="pguh-panel pguh-elearning-builder" onSubmit={saveModule}>
                <div className="pguh-panel__header">
                  <div>
                    <span className="pguh-eyebrow">{moduleDraft.id ? "Edit module" : "New module"}</span>
                    <h2 className="nhsuk-heading-l">Learning content</h2>
                  </div>
                  {moduleDraft.id && <button type="button" className="pguh-text-button" onClick={resetModuleDraft}>Create new instead</button>}
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="learning-title">Title</label>
                  <input id="learning-title" className="nhsuk-input" value={moduleDraft.title} onChange={(e) => setModuleDraft((d) => ({ ...d, title: e.target.value }))} required />
                </div>
                <div className="pguh-elearning-form-row">
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="learning-category">Category</label>
                    <input id="learning-category" className="nhsuk-input" value={moduleDraft.category} onChange={(e) => setModuleDraft((d) => ({ ...d, category: e.target.value }))} />
                  </div>
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="learning-minutes">Estimated minutes</label>
                    <input id="learning-minutes" className="nhsuk-input nhsuk-input--width-5" type="number" min="1" value={moduleDraft.estimated_minutes} onChange={(e) => setModuleDraft((d) => ({ ...d, estimated_minutes: e.target.value }))} />
                  </div>
                </div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="learning-description">Description</label>
                  <textarea id="learning-description" className="nhsuk-textarea" rows="4" value={moduleDraft.description} onChange={(e) => setModuleDraft((d) => ({ ...d, description: e.target.value }))} />
                </div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="learning-url">Learning resource link (optional)</label>
                  <input id="learning-url" className="nhsuk-input" type="url" placeholder="https://..." value={moduleDraft.content_url} onChange={(e) => setModuleDraft((d) => ({ ...d, content_url: e.target.value }))} />
                </div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="learning-notes">Instructions / learning notes</label>
                  <textarea id="learning-notes" className="nhsuk-textarea" rows="4" value={moduleDraft.content_notes} onChange={(e) => setModuleDraft((d) => ({ ...d, content_notes: e.target.value }))} />
                </div>

                <div className="nhsuk-checkboxes pguh-elearning-settings">
                  <div className="nhsuk-checkboxes__item">
                    <input className="nhsuk-checkboxes__input" id="learning-mandatory" type="checkbox" checked={moduleDraft.mandatory} onChange={(e) => setModuleDraft((d) => ({ ...d, mandatory: e.target.checked, quiz_enabled: e.target.checked ? true : d.quiz_enabled }))} />
                    <label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="learning-mandatory">Mandatory learning</label>
                    <div className="nhsuk-hint nhsuk-checkboxes__hint">Mandatory learning always requires a quiz.</div>
                  </div>
                  <div className="nhsuk-checkboxes__item">
                    <input className="nhsuk-checkboxes__input" id="learning-quiz" type="checkbox" checked={moduleDraft.mandatory || moduleDraft.quiz_enabled} disabled={moduleDraft.mandatory} onChange={(e) => setModuleDraft((d) => ({ ...d, quiz_enabled: e.target.checked }))} />
                    <label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="learning-quiz">Include quiz</label>
                  </div>
                </div>

                {(moduleDraft.mandatory || moduleDraft.quiz_enabled) && (
                  <>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="learning-pass-mark">Passing score (%)</label>
                      <input id="learning-pass-mark" className="nhsuk-input nhsuk-input--width-4" type="number" min="1" max="100" value={moduleDraft.passing_score} onChange={(e) => setModuleDraft((d) => ({ ...d, passing_score: e.target.value }))} />
                    </div>
                    <div className="pguh-elearning-question-builder">
                      <div className="pguh-panel__header">
                        <div><h3 className="nhsuk-heading-m">Quiz questions</h3><p>Exactly four answers are shown to staff; choose the correct one.</p></div>
                        <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => setQuestionDrafts((current) => [...current, EMPTY_QUESTION()])}><FaPlus /> Add question</button>
                      </div>
                      {questionDrafts.map((question, questionIndex) => (
                        <fieldset className="pguh-elearning-question-editor" key={question.id || questionIndex}>
                          <legend>Question {questionIndex + 1}</legend>
                          <label className="nhsuk-label">Question</label>
                          <input className="nhsuk-input" value={question.question_text} onChange={(e) => updateQuestion(questionIndex, { question_text: e.target.value })} />
                          <div className="pguh-elearning-answer-grid">
                            {question.options.map((option, optionIndex) => (
                              <div key={optionIndex}>
                                <label className="nhsuk-label">Answer {String.fromCharCode(65 + optionIndex)}</label>
                                <input className="nhsuk-input" value={option} onChange={(e) => updateOption(questionIndex, optionIndex, e.target.value)} />
                              </div>
                            ))}
                          </div>
                          <label className="nhsuk-label">Correct answer</label>
                          <select className="nhsuk-select" value={question.correct_index} onChange={(e) => updateQuestion(questionIndex, { correct_index: Number(e.target.value) })}>
                            {[0, 1, 2, 3].map((index) => <option key={index} value={index}>Answer {String.fromCharCode(65 + index)}</option>)}
                          </select>
                          <label className="nhsuk-label nhsuk-u-margin-top-3">Explanation (optional, Management reference)</label>
                          <textarea className="nhsuk-textarea" rows="2" value={question.explanation} onChange={(e) => updateQuestion(questionIndex, { explanation: e.target.value })} />
                          {questionDrafts.length > 1 && <button className="pguh-text-button pguh-text-button--danger" type="button" onClick={() => setQuestionDrafts((current) => current.filter((_, index) => index !== questionIndex))}>Remove question</button>}
                        </fieldset>
                      ))}
                    </div>
                  </>
                )}

                <button className="nhsuk-button" type="submit" disabled={working}><FaSave /> {working ? "Saving…" : moduleDraft.id ? "Save changes" : "Create learning"}</button>
              </form>

              <section className="pguh-panel">
                <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Catalogue</span><h2 className="nhsuk-heading-l">Existing learning</h2></div></div>
                <div className="pguh-elearning-catalogue-list">
                  {modules.length === 0 ? <p>No learning modules created yet.</p> : modules.map((module) => {
                    const counts = moduleCounts[module.id] || { assigned: 0, complete: 0 };
                    const questionCount = questions.filter((question) => question.module_id === module.id).length;
                    return (
                      <article key={module.id} className={`pguh-elearning-catalogue-card${!module.active ? " is-archived" : ""}`}>
                        <div>
                          <span className={module.mandatory ? "pguh-learning-pill is-mandatory" : "pguh-learning-pill"}>{module.mandatory ? "Mandatory" : "Development"}</span>
                          {!module.active && <span className="pguh-learning-pill">Archived</span>}
                          <h3>{module.title}</h3>
                          <p>{module.description || "No description."}</p>
                          <small>{questionCount} quiz question{questionCount === 1 ? "" : "s"} · {counts.complete}/{counts.assigned} complete</small>
                        </div>
                        <div className="pguh-elearning-card-actions">
                          <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => editModule(module)}>Edit</button>
                          <button className="pguh-text-button" type="button" onClick={() => toggleModuleActive(module)}><FaArchive /> {module.active ? "Archive" : "Restore"}</button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            </div>
          )}

          {tab === "assign" && (
            <div className="pguh-elearning-management-grid">
              <form className="pguh-panel pguh-elearning-assigner" onSubmit={assignLearning}>
                <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Assignment</span><h2 className="nhsuk-heading-l">Assign learning</h2></div></div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label">Learning module</label>
                  <select className="nhsuk-select" value={assignDraft.module_id} onChange={(e) => setAssignDraft((d) => ({ ...d, module_id: e.target.value }))} required>
                    <option value="">Select learning</option>
                    {modules.filter((row) => row.active).map((module) => <option key={module.id} value={module.id}>{module.mandatory ? "MANDATORY · " : ""}{module.title}</option>)}
                  </select>
                </div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label">Due date and time</label>
                  <input className="nhsuk-input" type="datetime-local" value={assignDraft.due_at} onChange={(e) => setAssignDraft((d) => ({ ...d, due_at: e.target.value }))} />
                  <div className="nhsuk-hint">Mandatory learning requires a due date. It is added to each assignee's staff calendar.</div>
                </div>

                <fieldset className="nhsuk-fieldset pguh-elearning-targets">
                  <legend className="nhsuk-fieldset__legend nhsuk-fieldset__legend--m">Who must complete it?</legend>
                  <div className="nhsuk-checkboxes">
                    <div className="nhsuk-checkboxes__item"><input className="nhsuk-checkboxes__input" id="target-all-staff" type="checkbox" checked={assignDraft.all_staff} onChange={(e) => setAssignDraft((d) => ({ ...d, all_staff: e.target.checked }))} /><label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="target-all-staff">All Staff accounts</label></div>
                    <div className="nhsuk-checkboxes__item"><input className="nhsuk-checkboxes__input" id="target-all-management" type="checkbox" checked={assignDraft.all_management} onChange={(e) => setAssignDraft((d) => ({ ...d, all_management: e.target.checked }))} /><label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="target-all-management">All Management accounts</label></div>
                  </div>

                  <h3 className="nhsuk-heading-s nhsuk-u-margin-top-5">Job roles</h3>
                  {roleOptions.length === 0 ? <p className="nhsuk-hint">Set staff job roles in the role directory opposite, then they can be targeted here.</p> : <div className="pguh-learning-role-chips">{roleOptions.map((role) => <label key={role} className={assignDraft.roles.includes(role) ? "is-selected" : ""}><input type="checkbox" checked={assignDraft.roles.includes(role)} onChange={() => toggleArrayValue("roles", role)} />{role}</label>)}</div>}

                  <h3 className="nhsuk-heading-s nhsuk-u-margin-top-5">Specific people</h3>
                  <div className="pguh-elearning-person-picker">
                    {profiles.map((person) => <label key={person.id}><input type="checkbox" checked={assignDraft.users.includes(person.id)} onChange={() => toggleArrayValue("users", person.id)} /><span><strong>{formatProfileName(person)}</strong><small>{person.job_role || person.position}</small></span></label>)}
                  </div>
                </fieldset>

                <div className="pguh-elearning-target-summary"><strong>{resolvedTargets.length}</strong><span>unique account{resolvedTargets.length === 1 ? "" : "s"} selected</span></div>
                <button className="nhsuk-button" disabled={working}>{working ? "Assigning…" : "Assign learning"}</button>
              </form>

              <section className="pguh-panel">
                <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Role directory</span><h2 className="nhsuk-heading-l">Staff job roles</h2></div></div>
                <p className="nhsuk-body-m">Management controls these role labels. They are used for role-based E-Learning assignment.</p>
                <div className="pguh-elearning-role-directory">
                  {profiles.map((person) => (
                    <div key={person.id}>
                      <span><strong>{formatProfileName(person)}</strong><small>{person.position} · {person.email}</small><small>{person.discord_id ? "Discord reminders connected" : "No Discord ID for reminders"}</small></span>
                      <input className="nhsuk-input" placeholder="e.g. Nurse, Doctor, HCA" value={person.job_role || ""} onChange={(e) => setProfiles((current) => current.map((row) => row.id === person.id ? { ...row, job_role: e.target.value } : row))} />
                      <button type="button" className="nhsuk-button nhsuk-button--secondary" disabled={roleSavingId === person.id} onClick={() => saveJobRole(person)}>{roleSavingId === person.id ? "Saving…" : "Save role"}</button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {tab === "compliance" && (
            <section className="pguh-panel pguh-elearning-compliance">
              <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Compliance</span><h2 className="nhsuk-heading-l">Assigned learning</h2></div><Link to="/intranet/operations?tab=notifications">Open staff notifications</Link></div>
              <input className="nhsuk-input nhsuk-u-margin-bottom-4" placeholder="Search staff, role, email or learning" value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="pguh-table-scroll">
                <table className="nhsuk-table nhsuk-table--striped">
                  <thead><tr><th>Staff member</th><th>Learning</th><th>Due</th><th>Status</th><th>Best score</th><th>Assigned via</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredAssignments.length === 0 ? <tr><td colSpan="7">No assignments found.</td></tr> : filteredAssignments.map((assignment) => {
                      const rowProgress = progressMap[assignment.id];
                      const status = statusForAssignment(assignment, rowProgress);
                      return <tr key={assignment.id} className={assignment.revoked_at ? "is-muted" : ""}><td><strong>{formatProfileName(assignment.user, "Unknown")}</strong><br /><small>{assignment.user?.job_role || assignment.user?.position || "Staff"}</small></td><td>{assignment.module?.mandatory && <span className="pguh-learning-pill is-mandatory">Mandatory</span>} {assignment.module?.title || "Unknown module"}</td><td>{formatDateTime(assignment.due_at)}</td><td><span className={`pguh-learning-status pguh-learning-status--${status.toLowerCase().replace(/\s+/g, "-")}`}>{assignment.revoked_at ? "Revoked" : status}</span></td><td>{rowProgress?.best_score == null ? "–" : `${rowProgress.best_score}%`}</td><td>{assignment.assigned_via || "Individual"}</td><td>{assignment.revoked_at ? "Revoked" : rowProgress?.status === "Completed" ? <FaCheckCircle aria-label="Completed" /> : <button className="pguh-text-button pguh-text-button--danger" type="button" onClick={() => revokeAssignment(assignment.id)}>Revoke</button>}</td></tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
